import React, { useEffect, useRef, useState } from 'react';
import { styles } from './styles';
import NovaeonMark from '../logo/logo';
import ReflectionButton from '../reflectionButton/reflectionButton';
import ThemeToggle from '../themeToggle/themeToggle';
import { COLORS, RIPPLE_ON_DARK } from '../../globalStyles';
import { scrollToId } from '../../utils';

interface BookendProps {
  isFooter?: boolean;
  onNavigate?: (sectionId: string) => void;
}

const NAV_LINKS = [
  { label: 'Services', id: 'services' },
  { label: 'Products', id: 'products' },
  { label: 'About', id: 'about' },
  { label: 'Contact', id: 'contact' },
];

const FOOTER_LINKS = [
  { label: 'Services', id: 'services' },
  { label: 'Products', id: 'products' },
  { label: 'About', id: 'about' },
  { label: 'Email us', href: 'mailto:hello@novaeon.io' },
  { label: 'Privacy', href: '/privacy' },
];

const Bookend: React.FC<BookendProps> = ({ isFooter = false, onNavigate }) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [footerWrapped, setFooterWrapped] = useState(false);
  const footerRef = useRef<HTMLElement | null>(null);
  const brandRef = useRef<HTMLDivElement | null>(null);
  const linksRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isFooter) return;
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';
      sections.forEach((s) => {
        if (window.scrollY >= (s as HTMLElement).offsetTop - 120) {
          current = s.id;
        }
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isFooter]);

  // Center the footer only when its two halves actually wrap onto separate
  // rows (font/zoom/viewport-driven), rather than at a fixed breakpoint. Wrap
  // is detected by the brand and links no longer sharing a vertical center.
  useEffect(() => {
    if (!isFooter) return;
    const check = () => {
      const b = brandRef.current;
      const l = linksRef.current;
      if (!b || !l) return;
      const bCenter = b.offsetTop + b.offsetHeight / 2;
      const lCenter = l.offsetTop + l.offsetHeight / 2;
      setFooterWrapped(Math.abs(lCenter - bCenter) > 4);
    };
    check();
    const ro = new ResizeObserver(check);
    if (footerRef.current) ro.observe(footerRef.current);
    window.addEventListener('resize', check);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [isFooter]);

  const scrollTo = (id: string) => {
    scrollToId(id);
    setMobileOpen(false);
    onNavigate?.(id);
  };

  const Logo = () => (
    <button style={styles.logo} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
      <NovaeonMark size={36} title="Novaeon" />
      <span style={styles.wordmark}>Novaeon</span>
    </button>
  );

  if (isFooter) {
    return (
      <footer
        ref={footerRef}
        style={{ ...styles.footer, justifyContent: footerWrapped ? 'center' : 'space-between' }}
      >
        <div
          ref={brandRef}
          style={{ ...styles.footerBrand, justifyContent: footerWrapped ? 'center' : undefined }}
        >
          <NovaeonMark size={34} title="Novaeon" />
          <span style={styles.footerCopy}>© {new Date().getFullYear()} Novaeon LLC. All rights reserved.</span>
        </div>
        <nav
          ref={linksRef}
          style={{
            ...styles.footerLinks,
            flexWrap: 'wrap',
            justifyContent: footerWrapped ? 'center' : undefined,
          }}
        >
          {FOOTER_LINKS.map((link) =>
            link.href ? (
              <a key={link.label} href={link.href} style={styles.footerLink}>
                {link.label}
              </a>
            ) : (
              <button key={link.label} style={styles.footerLink} onClick={() => scrollTo(link.id!)}>
                {link.label}
              </button>
            )
          )}
        </nav>
        <p style={styles.footerLegal}>
          This site is protected by reCAPTCHA and the Google{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
            style={styles.footerLegalLink}
          >
            Privacy Policy
          </a>{' '}
          and{' '}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer"
            style={styles.footerLegalLink}
          >
            Terms of Service
          </a>{' '}
          apply.
        </p>
      </footer>
    );
  }

  return (
    <header style={styles.header}>
      <Logo />
      <ul className="nav-links" style={styles.navLinks}>
        {NAV_LINKS.map((link) => (
          <li key={link.id}>
            <button
              style={{
                ...styles.navLink,
                color:
                  activeSection === link.id
                    ? COLORS.text
                    : hoveredLink === link.id
                      ? 'var(--nav-hover)'
                      : COLORS.muted,
              }}
              onMouseEnter={() => setHoveredLink(link.id)}
              onMouseLeave={() => setHoveredLink(null)}
              onClick={() => scrollTo(link.id)}
            >
              {link.label}
            </button>
          </li>
        ))}
      </ul>
      <div style={styles.headerRight}>
        <ReflectionButton
          className="header-cta"
          style={styles.navCta}
          highlightColor={RIPPLE_ON_DARK}
          onClick={() => scrollTo('contact')}
        >
          Get started
        </ReflectionButton>
        <ThemeToggle />
        <button
          className="menu-toggle"
          style={styles.menuToggle}
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          <span style={{ ...styles.menuBar, ...(mobileOpen ? styles.menuBarTop : {}) }} />
          <span style={{ ...styles.menuBar, opacity: mobileOpen ? 0 : 1 }} />
          <span style={{ ...styles.menuBar, ...(mobileOpen ? styles.menuBarBottom : {}) }} />
        </button>
      </div>

      {mobileOpen && (
        <div style={styles.mobileMenu}>
          {NAV_LINKS.map((link) => (
            <button key={link.id} style={styles.mobileLink} onClick={() => scrollTo(link.id)}>
              {link.label}
            </button>
          ))}
          <ReflectionButton
            style={styles.mobileCta}
            highlightColor={RIPPLE_ON_DARK}
            onClick={() => scrollTo('contact')}
          >
            Get started
          </ReflectionButton>
        </div>
      )}
    </header>
  );
};

export default Bookend;
